import { useState, useEffect } from 'react'
import { storeInstance } from 'store/createStore'
import { getClientPublicKey } from 'store/modules/user-session/selectors'
import { useCustomerConfig } from 'hooks/useCustomerConfig'
import { CustomerConfigKey } from 'modules/customer-config'

/**
 * Returns whether Provider Lookup is enabled for the current customer.
 * Defaults to true (ON) when the key is not configured, preserving
 * backward compatibility for all existing customers.
 *
 * Reads `clientPublicKey` from the Redux store singleton because this
 * repo uses react-redux v5, which doesn't expose the hooks API.
 */
export const useProviderLookupEnabled = (): {
  isEnabled: boolean
  isActive: boolean
  isLoading: boolean
} => {
  const [clientPublicKey, setClientPublicKey] = useState<string>(() =>
    getClientPublicKey(storeInstance.getState())
  )

  useEffect(() => {
    const sync = () => {
      const next = getClientPublicKey(storeInstance.getState())
      setClientPublicKey((prev) => (prev !== next ? next : prev))
    }
    const unsubscribe = storeInstance.subscribe(sync)
    // Reconcile: catch any store change that fired between useState init and subscribe
    sync()
    return unsubscribe
  }, [])

  const { data, isLoading } = useCustomerConfig(
    clientPublicKey,
    CustomerConfigKey.FEATURE__PROVIDER_LOOKUP_ENABLED
  )

  // Strict gate for provider UI (search, overview providers block) — off while loading
  // so disabled customers do not flash provider content.
  const isEnabled = isLoading ? false : data ?? true

  // Optimistic gate for stepper/navigation — on unless explicitly disabled.
  const isActive = data !== false

  return { isEnabled, isActive, isLoading }
}
